/**
 * Cliente HTTP del módulo Avisos. Una función por endpoint del backend.
 *
 * El endpoint POST /avisos devuelve LISTA porque la creación manual
 * genera N filas (una por destinatario). El frontend trata el array como
 * "se crearon estos avisos" — el agente normalmente verá solo los que
 * le tocan al refrescar el listado.
 */
import api from '../../../shared/services/api';
import type {
  Aviso,
  AvisoContadores,
  AvisoCreateManualPayload,
  AvisoEstado,
  AvisoTipo,
  AvisoUpdatePayload,
} from '../types';

export interface ListAvisosParams {
  estado?: AvisoEstado;
  tipo?: AvisoTipo;
  incluir_no_visibles?: boolean;
  limit?: number;
  offset?: number;
  /** Buscador libre: título, descripción, cliente, inmueble, operación y agente. */
  q?: string;
  /** Sólo admin/superadmin; el backend lo ignora para el resto de roles. */
  usuario_id?: number;
  /** Filtros por recurso — los usa el panel de histórico de los seguimientos. */
  lead_oficina_id?: number;
  lead_digital_id?: number;
  prospeccion_agente_id?: number;
}

export const listAvisos = async (params: ListAvisosParams = {}): Promise<Aviso[]> => {
  const response = await api.get<Aviso[]>('/avisos/', { params });
  return response.data;
};

/**
 * El scope (mine vs global) lo decide el backend según el rol del usuario:
 * admin/superadmin reciben el contador global del sistema, agentes solo el
 * suyo. No se envían parámetros.
 */
export const getContadores = async (): Promise<AvisoContadores> => {
  const response = await api.get<AvisoContadores>('/avisos/me/contadores');
  return response.data;
};

export const createAvisoManual = async (payload: AvisoCreateManualPayload): Promise<Aviso[]> => {
  const response = await api.post<Aviso[]>('/avisos/', payload);
  return response.data;
};

/**
 * Edita un aviso MANUAL. En los automáticos el backend responde 409 con un
 * mensaje explicativo — la UI ya impide llegar aquí, pero si llegara, el
 * `detail` del error es el que se enseña al usuario.
 */
export const updateAviso = async (id: number, payload: AvisoUpdatePayload): Promise<Aviso> => {
  const response = await api.patch<Aviso>(`/avisos/${id}`, payload);
  return response.data;
};

/** Devuelve un aviso resuelto/descartado a PENDIENTE. */
export const reabrirAviso = async (id: number): Promise<Aviso> => {
  const response = await api.patch<Aviso>(`/avisos/${id}/reabrir`);
  return response.data;
};

export const resolverAviso = async (id: number): Promise<Aviso> => {
  const response = await api.patch<Aviso>(`/avisos/${id}/resolver`);
  return response.data;
};

export const descartarAviso = async (id: number): Promise<Aviso> => {
  const response = await api.patch<Aviso>(`/avisos/${id}/descartar`);
  return response.data;
};

export const marcarLeido = async (id: number): Promise<Aviso> => {
  const response = await api.patch<Aviso>(`/avisos/${id}/leido`);
  return response.data;
};
